import cx from "classnames"
import { useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import Link from "components/Link"
import useIntersect from "effects/useIntersect"
import Announcement from "./Announcement"
import Banner from "./Banner"
import closeIcon from "./images/close.svg"
import darkLogo from "./images/preset-logo-dark.svg"
import lightLogo from "./images/preset-logo-light.svg"
import darkToggleMenuIcon from "./images/toggle-menu-dark.svg"
import lightToggleMenuIcon from "./images/toggle-menu-light.svg"
import NavItem from "./NavItem"
import NavMobile from "./NavMobile"
import SignUpButton from "components/ui/SignUpButton"

function Header({
  dark,
  floating,
  trackSignupButtonClick,
  pageName,
  pageBanner,
}) {
  const { contentfulGlobalSettings } = useStaticQuery(graphql`
    query NewHeaderQuery {
      contentfulGlobalSettings {
        title
        menus {
          header {
            label
            path
            position
            items {
              label
              description
              path
              icon
            }
          }
        }
        announcementBarId
        announcementBarText
        announcementBarCtaText
        announcementBarLink
      }
    }
  `)

  /** Header settings */
  const navItems = contentfulGlobalSettings?.menus?.header
  const announcementBarId = contentfulGlobalSettings?.announcementBarId
  const { announcementBarText } = contentfulGlobalSettings
  const { announcementBarCtaText } = contentfulGlobalSettings
  const announcementBarCtaLink = contentfulGlobalSettings.announcementBarLink

  /** Show announcement bar */
  const [showAnnouncement, setShowAnnouncement] = useState(false)
  useEffect(() => {
    if (
      announcementBarId &&
      announcementBarId !== localStorage.getItem("announcement_bar")
    ) {
      setShowAnnouncement(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementBarId])

  const hideAnnouncement = () => {
    setShowAnnouncement(false)
    localStorage.setItem("announcement_bar", announcementBarId)
  }

  /** Show banner on top */
  const bannerName = pageName ? pageName.replace(/-/g, "_") : ""

  const [showBanner, setShowBanner] = useState(false)
  useEffect(() => {
    if (pageBanner) {
      const { bannerId } = pageBanner
      if (
        bannerId &&
        bannerId !== localStorage.getItem(`${bannerName}_banner_id`)
      ) {
        setShowBanner(true)
      }
    }
  }, [bannerName, pageBanner])

  const hideBanner = () => {
    const { bannerId } = pageBanner
    setShowBanner(false)
    localStorage.setItem(`${bannerName}_banner_id`, bannerId)
  }

  /** Mobile toggle */
  const [isOpen, setIsOpen] = useState(false)
  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  /** Intersection observer */
  const [observer, intersecting] = useIntersect()

  /** Header theme */
  const isLightBackground = () => intersecting || !dark || isOpen

  function Logo() {
    return (
      <div className="w-full max-w-[250px]">
        <Link href="/" title="Home">
          <img
            className={cx("h-10", intersecting ? "lg:h-9" : "lg:h-9 xl:h-11")}
            src={isLightBackground() ? darkLogo : lightLogo}
            alt="Preset logo"
          />
        </Link>
      </div>
    )
  }

  function NavContainer({ children }) {
    return (
      <nav
        className={cx(
          "hidden flex-1 items-center justify-between space-x-9 font-bold lg:flex",
          intersecting ? "text-base" : "text-base xl:text-xl"
        )}
      >
        {children}
      </nav>
    )
  }

  function NavItems({ pos }) {
    return (
      <ul className="flex items-center space-x-9">
        {navItems
          ?.filter(({ position }) => position.toUpperCase() === pos)
          .map((props) => (
            <NavItem key={props.label} pos={pos} {...props} />
          ))}
        {pos === "RIGHT" && (
          <li>
            <SignUpButton
              size={intersecting ? "small" : "flexible"}
              dark={!isLightBackground()}
              trackClick={trackSignupButtonClick}
            >
              Try for Free
            </SignUpButton>
          </li>
        )}
      </ul>
    )
  }

  function ToggleMenu() {
    return (
      <button
        className="flex h-10 w-10 cursor-pointer items-center justify-center lg:hidden"
        onClick={() => toggleMenu()}
        type="button"
      >
        {isOpen ? (
          <img src={closeIcon} alt="Close" />
        ) : (
          <img
            src={isLightBackground() ? darkToggleMenuIcon : lightToggleMenuIcon}
            alt="Menu toggle"
          />
        )}
      </button>
    )
  }

  const classNames = cx("w-full transition-all sticky top-0 left-0 z-20", {
    "text-white": !isLightBackground(),
    "text-black": isLightBackground(),
    "bg-gradient-to-b from-white to-white shadow-simple lg:py-1":
      intersecting || isOpen,
    "bg-gradient-to-b from-white via-white to-transparent-white":
      !intersecting && !isOpen && !floating,
    "lg:py-10": !intersecting,
  })

  return (
    <>
      {showAnnouncement && (
        <Announcement
          id={announcementBarId}
          text={announcementBarText}
          ctaText={announcementBarCtaText}
          ctaLink={announcementBarCtaLink}
          onClick={() => hideAnnouncement()}
        />
      )}
      {showBanner && (
        <Banner
          id={pageBanner.bannerId}
          text={pageBanner.bannerText}
          link={pageBanner.bannerLink}
          onClick={() => hideBanner()}
        />
      )}
      <header className={classNames}>
        <div className="relative flex items-center justify-between px-5 py-5 lg:container lg:py-0">
          <Logo />
          <NavContainer>
            <NavItems pos="LEFT" />
            <NavItems pos="RIGHT" />
          </NavContainer>
          <ToggleMenu />
        </div>
        {isOpen && (
          <NavMobile
            items={navItems}
            trackSignupButtonClick={trackSignupButtonClick}
          />
        )}
      </header>
      <div ref={observer} />
    </>
  )
}

Header.propTypes = {
  /** If true, the default state will be dark (white logo and text) */
  dark: PropTypes.bool,
  /** If true, the header will have a white to transparent gradient background */
  floating: PropTypes.bool,
  /** Signup button click tracking event */
  trackSignupButtonClick: PropTypes.string,
  /** Page name */
  pageName: PropTypes.string,
}

export default Header
